import type {
  UpsellQueries_DifficultyLevel as DifficultyLevel,
  XdpV1_Org_Coursera_Degree_Stored_Api_UpsellType as UpsellTypes,
} from '__generated__/graphql-types';
import { Specialization_ProductVariant as S12nProductVariantValues } from '__generated__/graphql-types';
import type {
  UpsellQueries_InnerCourse as UpsellCourse,
  UpsellQueries_InnerMastertrack as UpsellMasterTrack,
  Specialization_Specialization as UpsellSpecialization,
} from '__generated__/graphql-types-do-not-use';
import Uri from 'jsuri';
import snakeCase from 'lodash/snakeCase';

import config from 'js/app/config';
import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type { PageTabName, Product } from '@coursera/event-pulse-types';

import {
  buildCourseUrlAbsolute,
  buildDegreeDescriptionUrlRelative,
  buildDegreeUrlRelative,
  buildMastertrackUrlAbsolute,
} from 'bundles/common/utils/urlUtils';
import type { EventCollectionType } from 'bundles/degree-description/components/modules/events/EventsSection';
import type {
  NavigationItem,
  StandaloneUpsellCoursesByDegreeSlug,
  UpsellsByDegreeSlug,
} from 'bundles/degree-description/types';

import _t from 'i18n!nls/degree-description';

export const DDP_TEMPLATE = {
  lightweightDdp: 'Lightweight DDP',
  fullDdp: 'Full DDP',
};

export const NAVIGATION_PARENT_ITEMS = {
  internalOnly: 'degree-description-page-internal',
  production: 'degree-description-page',
} as const;

export const getNavigationItems = () => [
  {
    id: 'overview',
    name: _t('Overview'),
    isIndexPage: true,
  },
  {
    id: 'admissions',
    name: _t('Admissions'),
  },
  {
    id: 'academics',
    name: _t('Academics'),
  },
  {
    id: 'tuition-financing',
    name: _t('Tuition & financing'),
  },
  {
    id: 'careers',
    name: _t('Careers'),
  },
  {
    id: 'student-experience',
    name: _t('Student experience'),
  },
];

export const LIGHTWEIGHT_NAVIGATION_ITEMS = [
  {
    id: 'overview',
    name: 'Overview',
    isIndexPage: true,
  },
  {
    id: 'tuition-financing',
    name: 'Tuition & financing',
  },
  {
    id: 'careers',
    name: 'Careers',
  },
  {
    id: 'admissions',
    name: 'Admissions',
  },
  {
    id: 'academics',
    name: 'Academics',
  },
  {
    id: 'student-experience',
    name: 'Student experience',
  },
];

// Values of type PageTabName from @coursera/event-pulse-types
export const PAGE_TABS = [
  'overview',
  'admissions',
  'academics',
  'tuition_financing',
  'student_experience',
  'careers',
  'about',
  'program',
  'courses',
  'instructors',
  'faq',
  'recommendations',
  'credit',
] as const;

/**
 * function to check validity of union type
 * @param input unknown value to be checked against a set of members
 * @param members readonly array that represents union type values
 * @returns boolean
 */
export const isMemberOfSet = <T>(input: unknown, members: readonly T[]): input is T => {
  const verifiedMember = members.find((member) => member === input);
  return !!verifiedMember;
};

export const isPageTabNameType = (input: unknown): input is PageTabName => isMemberOfSet(input, PAGE_TABS);

export const snakeCaseNavigationName = (inputString: string): PageTabName => {
  const parsedString = snakeCase(inputString);
  if (isPageTabNameType(parsedString)) return parsedString;
  throw new Error('This is not a valid click_page_tab event page tab name');
};

export const getElementHeight = (elm?: HTMLElement | null) => {
  if (!elm) return 0;
  const { top, bottom } = elm.getBoundingClientRect() ?? { top: 0, bottom: 0 };
  return bottom - top;
};

export const getCurrentNavItem = (
  navigationElements: (NavigationItem | null)[] | null,
  currentRouteName: string
): NavigationItem | undefined | null => {
  const currentNavElement = navigationElements?.find((navElement) => navElement?.id === currentRouteName);
  return currentNavElement;
};

export const isExternalLink = (url: string) => {
  const uri = new Uri(url);
  const hostName = uri.host().toString();
  return !hostName.includes(config.url.base);
};

/**
 * Builds either a link to `/degree-description/:route` or `/degrees/:route` depending on if we're viewing from `/degree-description/` or `/degrees/`, respectively.
 * @param slug The Degree program slug, e.g. `imba`
 * @param linkHref The route of the subpage we're on. `''` if it's the overview page, or e.g. `'/admissions'` if it's the admissions page
 * @param parentNavItem Determines if we're viewing the page from `/degree-description/` or `/degrees/`, pulled from the React router in this app
 * @returns A string that is full relative path, e.g. `/degrees/imba/admissions` or `/degree-description/imba/admissions`
 */
export const buildNavigationLink = (slug: string, linkHref: string, parentNavItem: string) => {
  let navLinkPath: string;

  switch (parentNavItem) {
    case NAVIGATION_PARENT_ITEMS.internalOnly:
      navLinkPath = buildDegreeDescriptionUrlRelative(slug, linkHref);
      break;
    case NAVIGATION_PARENT_ITEMS.production:
    default:
      navLinkPath = buildDegreeUrlRelative(slug, linkHref);
  }

  return navLinkPath;
};

export const humanizeDifficultyLevel = (level?: DifficultyLevel | null) => {
  if (!level) {
    return '';
  }

  return {
    BEGINNER: _t('Beginner'),
    INTERMEDIATE: _t('Intermediate'),
    ADVANCED: _t('Advanced'),
    UNDEFINED: '',
  }[level];
};

/**
 * Use this inside array.filter() to make typescript infer that the array will no longer have falsy elements after filtering.
 * This tends to be useful when an array of data returned from a graphql query is an array,
 * and that array could contain both objects and undefined items.
 * @example
 * const myArray: Array<string | undefined> = [ 'one', undefined, 'two' ];
 * const myFilteredArray: Array<string> = myArray.filter(filterNullOrUndefined); // [ 'one', 'two' ]
 */
export const filterNullOrUndefined = <T>(x: T | null | undefined): x is T => {
  return !!x;
};

export const getProductVariantString = (variant?: string | null): string => {
  switch (variant) {
    case S12nProductVariantValues.NormalS12N:
      return _t('Specialization');
    case S12nProductVariantValues.ProfessionalCertificateS12N:
      return _t('Professional Certificate');
    case S12nProductVariantValues.ExternalCertificateS12N:
      return _t('External Certificate');
    case S12nProductVariantValues.GoogleCertificateS12N:
      return _t('Google Certificate');
    case 'MasterTrack':
      return _t('MasterTrack®');
    case 'standardCourse':
      return _t('Course');
    case 'UniversityCertificate':
      return _t('University Certificate');
    default: {
      return '';
    }
  }
};

const eventingProductTypeMap = {
  Specialization: 's12n',
  Course: 'course',
  Degree: 'degree',
  'Premium Certificate': 'premium_certificate',
} as const;

export const productVariantToEventing = (variantName: string): Product['type'] | undefined => {
  const validProductTypes = Object.keys(eventingProductTypeMap);

  if (validProductTypes.find((validType) => validType === variantName)) {
    // TS doesn't handle Object.keys well. This tells TypeScript that it is the expected type at this point
    return eventingProductTypeMap[variantName as keyof typeof eventingProductTypeMap];
  } else {
    return undefined;
  }
};

export const getUpsellInfo = (upsellItem: UpsellsByDegreeSlug) => {
  if (upsellItem && upsellItem[0]) {
    const item = upsellItem[0];

    let upsell: UpsellSpecialization | UpsellCourse | UpsellMasterTrack | null;
    let upsellPartners;
    let upsellHeaderImage;
    let level;
    let url;
    let courses;
    let tagline;

    // if upsell is specialization
    if (
      item?.source?.upsellProduct?.__typename === 'UpsellQueries_Specialization' &&
      item?.source?.upsellProduct?.specializationDetails
    ) {
      upsell = item.source.upsellProduct.specializationDetails as UpsellSpecialization;
      upsellPartners = upsell.partners || [];
      upsellHeaderImage = upsell.cardImageUrl;
      level = upsell.difficultyLevel;
      url = `${config.url.domain}${upsell.urlPath}`;
      courses = upsell.courses;
      tagline = upsell.tagline;
    }

    // if upsell is course
    else if (item?.source?.upsellProduct?.__typename === 'UpsellQueries_Course' && item?.source?.upsellProduct.course) {
      upsell = item.source.upsellProduct.course as UpsellCourse;
      upsellPartners = upsell.partnerEntities || [];
      upsellHeaderImage = upsell.headerImage;
      level = upsell.level;
      url = upsell.slug ? buildCourseUrlAbsolute(upsell.slug) : ''; // slug should never be undefined
      courses = undefined;
      tagline = undefined;
    }

    // if upsell is mastertrack
    else if (
      item?.source?.upsellProduct?.__typename === 'UpsellQueries_Mastertrack' &&
      item?.source?.upsellProduct?.mastertrack
    ) {
      upsell = item.source.upsellProduct.mastertrack as UpsellMasterTrack;
      upsellPartners = upsell.partnerEntities || [];
      upsellHeaderImage = upsell.headerImage;
      level = undefined;
      url = upsell.slug ? buildMastertrackUrlAbsolute(upsell.slug) : ''; // slug should never be undefined
      courses = undefined;
      tagline = upsell.tagline;
    } else {
      return null;
    }
    return { upsell, upsellPartners, upsellHeaderImage, level, url, courses, tagline };
  } else return null;
};

export const filterOldEventsByDate = (events?: EventCollectionType | [], durationInDays = 7) => {
  if (!events?.length) return [];
  const currentDate = new Date();
  const previousDate = new Date();
  previousDate.setDate(currentDate.getDate() - durationInDays);

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event?.eventDate);
    return eventDate >= previousDate;
  });

  return filteredEvents;
};

export const filterStackableItemsByUpsellType = (
  stackableItems: UpsellsByDegreeSlug,
  filterUpsellType: UpsellTypes
) => {
  return stackableItems?.filter((upsell) => upsell.upsellType === filterUpsellType);
};

export const filterAndSliceStackableItemsByUpsellType = (
  stackableItems: UpsellsByDegreeSlug,
  filterUpsellType: UpsellTypes
) => {
  return filterStackableItemsByUpsellType(stackableItems, filterUpsellType);
};

export const getListOfStandaloneUpsellCourses = (
  allUpsellCourses?: UpsellsByDegreeSlug,
  standaloneUpsellCoursesByUpsellType?: StandaloneUpsellCoursesByDegreeSlug
) => {
  if (!allUpsellCourses) return null;
  const allUpsellCoursesMap = new Map();
  allUpsellCourses?.forEach((upsell) => {
    if (!!upsell && upsell.source.upsellProduct?.__typename === 'UpsellQueries_Course') {
      allUpsellCoursesMap.set(upsell.source.upsellProduct.course?.id, upsell);
    }
  });

  const listOfUpsellCoursesToThisDegree = [
    ...(standaloneUpsellCoursesByUpsellType?.offersCreditCourses ?? []),
    ...(standaloneUpsellCoursesByUpsellType?.partOfCourses ?? []),
    ...(standaloneUpsellCoursesByUpsellType?.relatedToCourses ?? []),
  ];

  return listOfUpsellCoursesToThisDegree
    .map((upsellCourse) => allUpsellCoursesMap.get(upsellCourse.id))
    .filter((upsellCourse) => !!upsellCourse);
};

export const getStackableItemsWithUpsellType = (
  listOfUpsellsLinkedtoThisDegree: UpsellsByDegreeSlug,
  standaloneUpsellCoursesByDegree: StandaloneUpsellCoursesByDegreeSlug
) => {
  const courseUpsells: UpsellsByDegreeSlug = listOfUpsellsLinkedtoThisDegree?.filter(
    (upsell) => !!upsell && upsell.source.upsellProduct?.__typename === 'UpsellQueries_Course'
  );
  const s12nUpsells: UpsellsByDegreeSlug = listOfUpsellsLinkedtoThisDegree?.filter(
    (upsell) => !!upsell && upsell.source.upsellProduct?.__typename === 'UpsellQueries_Specialization'
  );
  const mtcUpsells: UpsellsByDegreeSlug = listOfUpsellsLinkedtoThisDegree?.filter(
    (upsell) => !!upsell && upsell.source.upsellProduct?.__typename === 'UpsellQueries_Mastertrack'
  );

  const s12nUpsellsExist = s12nUpsells && s12nUpsells.length > 0;
  const mtcUpsellsExist = mtcUpsells && mtcUpsells.length > 0;

  let allUpsells: UpsellsByDegreeSlug = [];
  // Prioritization of upsells: s12ns > MTs > courses
  const sortedStandaloneCourseUpsells = getListOfStandaloneUpsellCourses(
    courseUpsells,
    standaloneUpsellCoursesByDegree
  );
  const sortedAndFilteredCourseUpsellsExist = sortedStandaloneCourseUpsells && sortedStandaloneCourseUpsells.length > 0;
  allUpsells = [
    ...(s12nUpsellsExist ? s12nUpsells : []),
    ...(mtcUpsellsExist ? mtcUpsells : []),
    ...(sortedAndFilteredCourseUpsellsExist ? sortedStandaloneCourseUpsells : []),
  ];

  return allUpsells;
};

export const getIsValidDeadlineCardDate = (date: string | null | undefined) => {
  if (
    !date ||
    !formatDateTimeDisplay(date, MOMENT_FORMATS.TIMESTAMP) ||
    formatDateTimeDisplay(date, MOMENT_FORMATS.TIMESTAMP) === 'Invalid date'
  ) {
    return false;
  }
  return true;
};
